import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './App.css';
import NavSection from './components/NavSection';
import Footer from './components/Footer';
import Autocomplete from './Autocomplete';
import bg from './bg.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTurnDown, faArrowTurnUp } from '@fortawesome/free-solid-svg-icons';

const API_BASE = 'https://api-rggjpj4zxq-uc.a.run.app';

function App() {
  const [businessDetails, setBusinessDetails] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.places);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const toggleReadMore = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const resetResultState = () => {
    setReviews([]);
    setShowReviews(false);
    setShowModal(false);
  };

  const updateMap = useCallback(() => {
    if (!businessDetails?.placeId || !window.google?.maps || !mapsReady) return;

    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(mapContainer, { zoom: 15 });
    }

    const service = new window.google.maps.places.PlacesService(mapRef.current);
    service.getDetails(
      {
        placeId: businessDetails.placeId,
        fields: ['geometry', 'name'],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          console.error('Error loading map details:', status);
          return;
        }

        if (place?.geometry?.location) {
          mapRef.current.setCenter(place.geometry.location);
          mapRef.current.setZoom(15);

          if (markerRef.current) {
            markerRef.current.setMap(null);
          }

          markerRef.current = new window.google.maps.Marker({
            map: mapRef.current,
            position: place.geometry.location,
            title: place.name,
          });
        }
      }
    );
  }, [businessDetails, mapsReady]);

  const loadGoogleMapsScript = useCallback(async () => {
    try {
      if (window.google?.maps?.places) {
        setMapsReady(true);
        return;
      }

      const response = await fetch(`${API_BASE}/get-google-maps-script`);
      if (!response.ok) {
        console.error('Failed to fetch Google Maps API script URL:', response.statusText);
        return;
      }

      const data = await response.json();
      const scriptUrl = data.scriptUrl;

      const existing = document.querySelector(`script[src="${scriptUrl}"]`);
      if (existing) {
        if (window.google?.maps?.places) {
          setMapsReady(true);
        } else {
          existing.addEventListener('load', () => setMapsReady(true));
        }
        return;
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps API loaded successfully');
        setMapsReady(true);
      };
      script.onerror = (error) => {
        console.error('Error loading Google Maps API:', error);
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error fetching Google Maps API URL:', error);
    }
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (businessDetails?.placeId) {
      updateMap();
    }
  }, [businessDetails, updateMap]);

  const handlePlaceSelected = (place) => {
    setBusinessName(place.name || '');
    setSelectedPlaceId(place.placeId || null);
    resetResultState();
  };

  const handleQueryChange = (value) => {
    setBusinessName(value);
    setSelectedPlaceId(null);
  };

  const handleFetchDetails = async () => {
    if (!businessName && !selectedPlaceId) {
      alert('Please enter a business name');
      return;
    }

    try {
      resetResultState();

      const params = new URLSearchParams();
      if (selectedPlaceId) {
        params.set('placeid', selectedPlaceId);
      }
      if (businessName) {
        params.set('name', businessName);
      }

      const response = await fetch(`${API_BASE}/get-business-details?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setBusinessDetails(data);
        setSelectedPlaceId(data.placeId || selectedPlaceId);
      } else {
        console.error('Failed to fetch:', response.statusText);
        alert('Could not fetch business details. Please try another business.');
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      alert('Error fetching business details. Please try again.');
    }
  };

  const handleShowReviews = async () => {
    if (!businessDetails || !businessDetails.placeId) return;

    try {
      const response = await fetch(
        `${API_BASE}/get-business-reviews?placeid=${encodeURIComponent(businessDetails.placeId)}`
      );
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews.slice(0, 5));
        setShowReviews(true);
      } else {
        console.error('Failed to fetch reviews:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleHideReviews = () => {
    setShowReviews(false);
  };

  const handleMoreInfo = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleViewOnGoogleMap = () => {
    window.open(`https://www.google.com/maps/search/?q=${businessDetails.businessLink}`, '_blank');
  };

  const handleSearchAgain = () => {
    setBusinessDetails(null);
    setBusinessName('');
    setSelectedPlaceId(null);
    resetResultState();
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    mapRef.current = null;
    window.location.reload();
  };

  return (
    <div>
      <Helmet>
        <title>Review Link Generator</title>
      </Helmet>
      <div>
        <NavSection />
        <div
          className="container"
          style={{
            backgroundImage: `url(${bg})`,
          }}
        >
          <h1 className="heading">
            Google Business Profile <span className="highlight">Review Link</span> Generator
          </h1>
          <center>
            <p className="description">
              With a single click, now you can generate a Google review link for your business and
              share it with your customers.
            </p>
          </center>

          <div className="input-group search-row">
            <Autocomplete
              onPlaceSelected={handlePlaceSelected}
              onQueryChange={handleQueryChange}
            />
            <button className="fetch-button" onClick={handleFetchDetails}>
              Fetch Details
            </button>
          </div>

          {businessDetails && (
            <div className="cards-container">
              <div className="card">
                <h3>Share your Google Reviews</h3>
                <p>Share the link with your customers to show your Google Listing reviews.</p>
                <a href={businessDetails.reviewLink} target="_blank" rel="noopener noreferrer">
                  [{businessDetails.reviewLink.slice(0, 11)}...] Click here
                </a>
                <br />
                <br />
                {!showReviews ? (
                  <button onClick={handleShowReviews}>
                    Show Reviews <FontAwesomeIcon icon={faArrowTurnDown} />
                  </button>
                ) : (
                  <button onClick={handleHideReviews}>
                    Hide Reviews <FontAwesomeIcon icon={faArrowTurnUp} />
                  </button>
                )}
              </div>

              <div className="card">
                <h3>Get Reviews</h3>
                <p>Share this link so that customers can write a review without additional effort.</p>
                <a
                  href={businessDetails.writeReviewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [{businessDetails.writeReviewLink.slice(0, 11)}...] Click here
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(businessDetails.writeReviewLink);
                  }}
                >
                  Copy & Share
                </button>
              </div>

              <div className="card">
                <h3>View your Google Listing in SERP</h3>
                <p>
                  Click the link and see how your business and associated information appear in
                  search result.
                </p>
                <a href={businessDetails.serplink} target="_blank" rel="noopener noreferrer">
                  [{businessDetails.serplink.slice(0, 11)}...] Click here
                </a>
                <button
                  onClick={() => {
                    window.open(businessDetails.serplink, '_blank');
                  }}
                >
                  View In SERP
                </button>
              </div>
            </div>
          )}

          {showReviews && (
            <div className="reviews-container">
              {reviews.map((review, index) => (
                <div key={index} className="review-card">
                  <div className="review-content">
                    <p>
                      <strong>{review.authorName}</strong>
                    </p>
                    <p>
                      <em>{review.date}</em>
                    </p>
                    <p>{review.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {businessDetails && (
            <div className="map-container">
              <div id="map"></div>

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button onClick={handleMoreInfo}>More Information</button>
                <button onClick={handleViewOnGoogleMap}>View On Google Map</button>
                <button onClick={handleSearchAgain}>Search Again</button>
              </div>
            </div>
          )}

          {showModal && businessDetails && (
            <div className="modal">
              <div className="modal-content">
                <h2>Business Information</h2>
                <p>
                  <strong>Place ID:</strong> {businessDetails.placeId}
                </p>
                <p>
                  <strong>Ludocid:</strong> {businessDetails.cid}
                </p>
                <p>
                  <strong>Address:</strong> {businessDetails.address}
                </p>
                <p>
                  <strong>Phone Number:</strong> {businessDetails.phone}
                </p>
                <p>
                  <strong>Website:</strong> {businessDetails.website}
                </p>
                <p>
                  <strong>Business Status:</strong> {businessDetails.status}
                </p>
                <p>
                  <strong>Business Hours:</strong> {businessDetails.hours}
                </p>
                <p>
                  <strong>Current Opening Status:</strong> {businessDetails.openingStatus}
                </p>
                <button onClick={handleCloseModal}>Close</button>
              </div>
            </div>
          )}

          <div className="faq-container">
            <h2 className="faq-heading">FAQ</h2>
            <div className="faq-cards-container">
              <div className="faq-card">
                <h3>What is Ludocid?</h3>
                <p>
                  {expandedCard === 1 ? (
                    <>
                      Ludocid or CID (customer ID number) is a distinct individual ID that Google
                      allocates to a business location making it convenient for Google to ascertain
                      the identity of the business across multiple Google platforms. This helps
                      Google manage the business and its presence across its platforms such as Google
                      Maps, Google Search, and other Google services.
                    </>
                  ) : (
                    'Ludocid or CID (customer ID number) is a distinct individual ID that Google allocates to a business location making it convenient for Google to ascertain the identity of the business across multiple Google platforms.'
                  )}
                </p>
                <button className="read-more-btn" onClick={() => toggleReadMore(1)}>
                  {expandedCard === 1 ? 'Show Less' : 'Read More'}
                </button>
              </div>
              <div className="faq-card">
                <h3>How Can Ludocid Be Used?</h3>
                <p>
                  {expandedCard === 2 ? (
                    <>
                      The Ludocid serves various purposes from helping identify business on Google
                      search results & Google Maps to analyzing the performance of the business
                      listing across various Google products. In addition to that, a ‘CID’ can be
                      used to manage listings, support & tackle issues with a business profile, and
                      link multiple Google profiles, improving business management on Google
                      platforms.
                    </>
                  ) : (
                    'The Ludocid serves various purposes from helping identify business on Google search results & Google Maps to analyzing the performance of the business listing across various Google products.'
                  )}
                </p>
                <button className="read-more-btn" onClick={() => toggleReadMore(2)}>
                  {expandedCard === 2 ? 'Show Less' : 'Read More'}
                </button>
              </div>
              <div className="faq-card">
                <h3>What is Place ID?</h3>
                <p>
                  {expandedCard === 3 ? (
                    <>
                      The place ID, as the name suggests, is a standalone textual identifier
                      associated with a place like business, landmark, or intersection that is
                      referred to by Google to identify the specific business within its platforms.
                      It helps in the accurate identification and mapping of the business location
                      across various Google services, allowing for better search results and
                      business visibility.
                    </>
                  ) : (
                    'The place ID as the name suggests is a standalone textual identifier associated with a place like business, landmark, or intersection that is referred to by Google to identify the specific business within its platforms.'
                  )}
                </p>
                <button className="read-more-btn" onClick={() => toggleReadMore(3)}>
                  {expandedCard === 3 ? 'Show Less' : 'Read More'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default App;
